import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface Props {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Rendered below the subtitle — e.g. slug + edit affordance, secondary metadata. */
  meta?: ReactNode;
  /** Rendered top-right — action buttons or badges. */
  actions?: ReactNode;
  /** Rendered top-right, before actions — status badge slot. */
  status?: ReactNode;
  className?: string;
}

// Unified page-title area. Every page's h1 + subtitle + right-side actions
// composition goes through this so spacing/typography stays consistent.
// The visible <h1> is `data-testid="page-header-title"` so tests can pin the
// title without knowing which page they're on.
//
// Sits on the plain page background (bg-muted/40 from DashboardLayout) — no
// card shell. Content sections below use `<Card>` for their own chrome; the
// header is deliberately unwrapped so page hierarchy reads "here's what this
// page is about" (title) then "here's the content" (cards) rather than
// "card, card, card". This matches the AuditLog layout on the ask from
// Chunk 5B follow-up feedback.
export function PageHeader({ title, subtitle, meta, actions, status, className }: Props) {
  return (
    <header
      className={cn('flex items-start justify-between gap-4', className)}
      data-testid="page-header"
    >
      <div className="min-w-0 flex-1">
        <h1
          className="truncate text-2xl font-semibold text-foreground"
          data-testid="page-header-title"
        >
          {title}
        </h1>
        {subtitle && (
          <div className="mt-1 text-sm text-muted-foreground" data-testid="page-header-subtitle">
            {subtitle}
          </div>
        )}
        {meta && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {meta}
          </div>
        )}
      </div>
      {(status || actions) && (
        <div className="flex shrink-0 items-start gap-2">
          {status}
          {actions}
        </div>
      )}
    </header>
  );
}
